import axios, { AxiosResponse } from "axios";
import { Product, ProductSearchFilters, ProductSort, ProductType } from "../types/api";

import {
  AUDIT_EVENT_API,
  INV_API,
  LOCS_API,
  NEXT_NUMBER_API,
  NN_ACCESSORY_KEY,
  NN_BOOK_KEY,
  NN_GIFT_KEY,
  NN_INSTRUMENT_KEY,
  NN_OTHER_KEY,
  ProductTypePrefix,
} from "../config";

export const getProducts = async (
  options: { skip?: number; take?: number },
  dataCallback: (data: any) => void,
  totalRowsCallback: (data: any) => void,
  errorCallback?: (data: any) => void
) => {
  const skip = options?.skip || 0;
  const take = options?.take || 25;

  axios
    .get(INV_API, { params: { skip: skip, take: take, includeVariants: true, includeImageUrls: true } })
    .then((r) => {
      dataCallback(r.data);

      if (totalRowsCallback) {
        axios.get(INV_API + "count").then((c) => {
          totalRowsCallback(c.data.rows);
        });
      }
    })
    .catch((e) => {
      if (errorCallback) {
        errorCallback(e);
      }
    });
};

export interface ProductQueryOptions {
  skip?: number;
  take?: number;
  search?: string;
  filters?: ProductSearchFilters;
  sort?: ProductSort | null;
}

// Loads a page of products, optionally narrowed by a search term and/or the
// advanced search filters. Handles both response shapes from GET /products:
// a plain array (no search, no filters - count fetched separately) or
// { data, totalCount } (search and/or filters applied server-side).
export const fetchProducts = async (
  options: ProductQueryOptions,
  dataCallback: (data: Product[]) => void,
  totalRowsCallback: (total: number) => void,
  errorCallback?: (error: any) => void
) => {
  const params: Record<string, any> = {
    skip: options.skip || 0,
    take: options.take || 25,
    includeVariants: true,
    includeImageUrls: true,
  };

  if (options.search) {
    params.search = options.search;
  }

  if (options.sort) {
    params.sortBy = options.sort.field;
    params.sortDir = options.sort.dir;
  }

  const filters = options.filters;
  if (filters) {
    if (filters.stock && filters.stock !== "all") params.stock = filters.stock;
    if (filters.sale && filters.sale !== "all") params.sale = filters.sale;
    if (filters.supplierGuid) params.supplierGuid = filters.supplierGuid;
    if (filters.location) params.location = filters.location;
  }

  axios
    .get(INV_API, { params })
    .then((r) => {
      if (Array.isArray(r.data)) {
        dataCallback(r.data);
        axios
          .get(INV_API + "count")
          .then((c) => totalRowsCallback(c.data.rows))
          .catch((e) => errorCallback && errorCallback(e));
      } else {
        dataCallback(r.data.data || []);
        if (r.data.totalCount !== undefined) {
          totalRowsCallback(r.data.totalCount);
        }
      }
    })
    .catch((e) => {
      if (errorCallback) {
        errorCallback(e);
      }
    });
};

export const searchProducts = async (
  options: { skip?: number; take?: number; search: string },
  dataCallback: Function,
  totalRowsCallback?: Function
) => {
  const skip = options.skip || 0;
  const take = options.take || 100;
  const search = options.search;

  axios.get(INV_API, { params: { skip: skip, take: take, search: search, includeVariants: true, includeImageUrls: true } }).then((r) => {
    // Server returns { data: products[], totalCount: number } for search queries
    const products = r.data.data || r.data;
    const totalCount = r.data.totalCount;

    dataCallback(products);

    if (totalRowsCallback && totalCount !== undefined) {
      totalRowsCallback(totalCount);
    }
  });
};

export const getProductsForLocation = async (
  location: string,
  dataCallback: Function
) => {
  axios.get(`${INV_API}forLocation/${encodeURIComponent(location)}`).then((r) => {
    dataCallback(r.data);
  });
}

export const getProduct = async (guid: string) => {
  return (await axios.get(`${INV_API}${guid}`, { params: { includeVariants: true, includeImageUrls: true } })).data;
}

export const getProductWithVariantGuid = async (guid: string) => {
  return (await axios.get(`${INV_API}variant/${guid}`, { params: { includeVariants: true, includeImageUrls: true } })).data[0];
};

export const getInventoryForVariants = async (guids: string, callback: Function) => {
  // Technically this should be a GET, but the list of guids can be quite long, so has to be sent as a body payload,
  // which axios does not support in a get request
  axios.post(`${INV_API}inventory`, {guids: guids }).then((r) => callback(r.data));
}

// Updates stock level and prices in the inventory service
export const updateVariantStockLevelAndPrices = async (
  guid: string,
  delta: number,
  retailPrice?: number,
  costPrice?: number
) => {
  return axios.put(`${INV_API}${guid}/update-stock?updateShopify=true`, {
    stockLevelDelta: delta,
    newRetailPrice: retailPrice,
    newBuyPrice: costPrice,
  });
};

interface CreateOrUpdateOptions {
  product: Product;
  success: Function;
  failure?: Function;
}

/**
 * Turns an axios error into a message fit for the error toast. Prefers the
 * server's own message (e.g. the 409 "name field has already been used"),
 * then falls back to the given default.
 */
export const saveErrorMessage = (e: any, fallback: string): string => {
  const data = e?.response?.data;
  const message = Array.isArray(data?.message) ? data.message.join(", ") : data?.message;
  return typeof message === "string" && message.trim() !== "" ? message : fallback;
};

export const createProduct = async (options: CreateOrUpdateOptions) => {
  const { product, success, failure } = options;

  // Create SKUs for all the variants
  const key =
    product.productType === ProductType.BOOK
      ? NN_BOOK_KEY
      : product.productType === ProductType.ACCESSORY
      ? NN_ACCESSORY_KEY
      : product.productType === ProductType.GIFT
      ? NN_GIFT_KEY
      : product.productType === ProductType.INSTRUMENT
      ? NN_INSTRUMENT_KEY
      : product.productType === ProductType.OTHER
      ? NN_OTHER_KEY
      : undefined;

  if (!key) {
    return failure(
      new Error(`Don't recognise product type - ${product.productType}`)
    );
  }

  const r: AxiosResponse = await axios.get(NEXT_NUMBER_API + key);
  const prefix = `${ProductTypePrefix[product.productType]}${r.data.value
    .toString()
    .padStart(5, "0")}`;

  if (product.variants.length === 1) {
    // A single variant, so assign the basic SKU
    product.variants[0].sku = prefix;
  } else {
    // Generate some delimiters based on the filterValues
    product.variants.forEach((v) => {
      console.log(v);
      const filterStr = Array.isArray(v.filterValues) 
        ? v.filterValues.join(",") 
        : v.filterValues || "";
      const sku = `${prefix}-${filterStr
        .toUpperCase()
        .replaceAll(/[AEIOU]/g, "")
        .split(/\s*,\s*/)
        .join("-")}`;
      v.sku = sku;
    });
  }

  // Remove cyclic references from the product, as well as ID fields used previously for display purposes
  product.id = undefined;
  product.variants.forEach((v) => {
    v["id"] = v["product"] = v["productId"] = undefined;
    // imageUrls are now strings, no need to clear productVariantId
  });

  axios
    .post(INV_API + "create", product)
    .then((r) => {
        success(r);
    })
    .catch((e) => {
      failure(saveErrorMessage(e, "Unable to create product"));
    });
};

export const updateProduct = (options: CreateOrUpdateOptions) => {
  const { product, success, failure } = options;

  product.id = undefined;

  // Remove things from the product object that Prisma won't like. Possible no variants were passed as part of the update.
  if (product.variants) product.variants.forEach((v) => {
    v["productId"] = undefined;
    v["product"] = undefined;
    if (+v["id"] < 0) v["id"] = undefined;
    // imageUrls are now strings, no need to clear productVariantId
  });

  axios
    .put(`${INV_API}${product.guid}/update?updateShopify=true`, product)
    .then((r) => {
      success(r);
    })
    .catch((e) => {
      failure(saveErrorMessage(e, "Unable to update product"));
    });
};

export const deleteProduct = (options: CreateOrUpdateOptions) => {
  const { product, success, failure } = options;

  if (product.guid) {
    axios
      .delete(`${INV_API}${product.guid}`)
      .then((r) => {
        success(r);
      })
      .catch((e) => {
        if (failure) {
          failure(e);
        }
      });
  }
};

export const getSaleEvents = (sku: string, success, failure) => {
  axios
    .get(`${AUDIT_EVENT_API}sales?targetId=${sku}`)
    .then((r) => success(r.data))
    .catch((e) => {
      if (failure) {
        failure(e);
      }
    });
};

export const getLocations = (success: (r: any) => void, error: (r: any) => void) => {
  axios.get(`${LOCS_API}`)
  .then((r) => {
    success(r.data.map((loc) => loc.location));
  })
  .catch((e) => {
    error(e);
  })
}

export const updateLocation = (guid: string, location: string, success: (r: any) => void, error?: (r: any) => void) => {
  axios
    .put(`${INV_API}${guid}/update-location/${encodeURIComponent(location)}`)
    .then((r) => success(r.data))
    .catch((e) => {
      if (error) {
        error(e);
      }
    });
}

export interface MoveLocationResult {
  count: number;
  location: string;
  moved: { guid: string; name: string; from: string }[];
}

// Bulk move of products to a location. Location is not held in Shopify, so
// this is a single server-side update with no sync.
export const moveProductsToLocation = async (
  guids: string[],
  location: string
): Promise<MoveLocationResult> => {
  const r = await axios.put(`${INV_API}move-location`, { guids, location });
  return r.data;
};

export interface UpdateSupplierResult {
  count: number;
  supplier: { guid: string; name: string };
  updated: { guid: string; name: string; fromSupplierGuid: string | null; fromSupplierName: string | null }[];
}

// Bulk supplier change. Supplier is not sent to Shopify (vendor there is the
// brand) nor to Xero, so this is a single server-side update with no sync.
export const updateProductsSupplier = async (
  guids: string[],
  supplierGuid: string
): Promise<UpdateSupplierResult> => {
  const r = await axios.put(`${INV_API}update-supplier`, { guids, supplierGuid });
  return r.data;
};

export interface UpdateTagsResult {
  count: number;
  changed: number;
  add: string[];
  remove: string[];
  products: { guid: string; name: string; tags: string[] }[];
  shopifySyncQueued: number;
}

// Bulk tag change. The database is updated in one call; published products
// are re-synced to Shopify in the background on the server, and the outcome
// is recorded in the audit log.
export const updateProductsTags = async (
  guids: string[],
  add: string[],
  remove: string[]
): Promise<UpdateTagsResult> => {
  const r = await axios.put(`${INV_API}update-tags`, { guids, add, remove });
  return r.data;
};

export const renameLocation = (oldLocation: string, newLocation: string, success: (r: any) => void, error?: (r: any) => void) => {
  axios
    .put(`${LOCS_API}${encodeURIComponent(oldLocation)}/rename-location/${encodeURIComponent(newLocation)}`)
    .then((r) => success(r.data))
    .catch((e) => {
      if (error) {
        error(e);
      }
    });
}

export const auditStockAdjustment = async (
  sku: string,
  quantity: number,
  reason: string
) => {
  return axios.post(`${AUDIT_EVENT_API}adjust`, {
    sku: sku,
    quantity: quantity,
    reason: reason,
    date: new Date(),
  });
};
